// src/components/payments/ItineraryPaymentSheet.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const initialContact = {
  firstName: '',
  lastName: '',
  email: ''
};

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function formatAmount(amountCents, currency) {
  if (!Number.isFinite(amountCents)) return null;
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase?.() || 'USD',
    });
    return formatter.format(amountCents / 100);
  } catch (error) {
    console.warn('[ItineraryPaymentSheet] Failed to format amount', error);
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}

function ContactForm({
  contact,
  onChange,
  onSubmit,
  onCancel,
  saving,
  error,
  validationErrors,
  loadError,
  showPromo,
  onTogglePromo,
  promoCode,
  onPromoChange,
  promoError,
  submitLabel,
}) {
  const handleChange = (field) => (event) => {
    onChange((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <p className="text-sm text-white/80">
        We’ll email your receipt and the completed itinerary to the address below. Update it if anything looks off.
      </p>

      {loadError && (
        <div className="rounded-md border border-yellow-400/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-200">
          {loadError}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-white/60">First name</label>
          <input
            type="text"
            value={contact.firstName}
            onChange={handleChange('firstName')}
            className="mt-1 w-full rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-white shadow-inner focus:border-emerald-400/60 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            autoComplete="given-name"
          />
          {validationErrors.firstName && (
            <p className="mt-1 text-xs text-red-300">{validationErrors.firstName}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-white/60">Last name</label>
          <input
            type="text"
            value={contact.lastName}
            onChange={handleChange('lastName')}
            className="mt-1 w-full rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-white shadow-inner focus:border-emerald-400/60 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            autoComplete="family-name"
          />
          {validationErrors.lastName && (
            <p className="mt-1 text-xs text-red-300">{validationErrors.lastName}</p>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-white/60">Email for receipt + itinerary</label>
        <input
          type="email"
          value={contact.email}
          onChange={handleChange('email')}
          className="mt-1 w-full rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-white shadow-inner focus:border-emerald-400/60 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
          autoComplete="email"
        />
        {validationErrors.email && (
          <p className="mt-1 text-xs text-red-300">{validationErrors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        {!showPromo ? (
          <button
            type="button"
            onClick={onTogglePromo}
            className="text-xs font-semibold text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
          >
            I have a discount code
          </button>
        ) : (
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wide text-white/60">Discount code</label>
              <button
                type="button"
                onClick={onTogglePromo}
                className="text-[11px] font-medium text-white/60 hover:text-white/80"
              >
                Remove
              </button>
            </div>
            <input
              type="text"
              value={promoCode}
              onChange={(event) => onPromoChange(event.target.value)}
              className="mt-1 w-full rounded-md border border-white/10 bg-slate-900/60 px-3 py-2 text-white shadow-inner uppercase tracking-wide focus:border-emerald-400/60 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
            />
            {promoError && (
              <p className="mt-1 text-xs text-red-300">{promoError}</p>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center md:justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary w-full md:w-auto"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary-cta w-full md:w-auto"
          disabled={saving}
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function PaymentForm({
  intent,
  onSuccess,
  onClose,
  setStatus,
  setError,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setStatus('submitting');
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || 'Please verify the payment details.');
      setSubmitting(false);
      setStatus('ready');
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message || 'We could not confirm the payment. Please try again.');
      setSubmitting(false);
      setStatus('ready');
      return;
    }

    setSubmitting(false);
    setStatus('succeeded');
    onSuccess(paymentIntent);
  }, [elements, onSuccess, setError, setStatus, stripe]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement options={{ layout: 'tabs' }} />

      <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center md:justify-between">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary w-full md:w-auto"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary-cta w-full md:w-auto"
          disabled={submitting}
        >
          {submitting ? 'Processing…' : `Confirm ${formatAmount(intent?.amount, intent?.currency) || 'purchase'}`}
        </button>
      </div>
    </form>
  );
}

export default function ItineraryPaymentSheet({
  isOpen,
  itineraryId,
  versionId,
  onClose,
  onSuccess,
  onError,
}) {
  const { island } = useMemo(() => getRuntimeSiteContext(), []);
  const [step, setStep] = useState('contact');
  const [status, setStatus] = useState('idle');
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState(initialContact);
  const [contactErrors, setContactErrors] = useState({});
  const [contactLoadError, setContactLoadError] = useState(null);
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep('contact');
      setStatus('idle');
      setIntent(null);
      setError(null);
      setContact(initialContact);
      setContactErrors({});
      setContactLoadError(null);
      setShowPromo(false);
      setPromoCode('');
      setPromoError(null);
      return;
    }

    if (!publishableKey || !stripePromise) {
      setError('Stripe is not configured. Please contact support.');
      setStep('contact-error');
      return;
    }

    if (!versionId) {
      setError('We could not identify this itinerary version. Please refresh and try again.');
      setStep('contact-error');
      return;
    }

    let cancelled = false;
    const fetchContact = async () => {
      setStep('contact-loading');
      setContactErrors({});
      setError(null);
      try {
        const interviewRef = doc(db, 'interviews', itineraryId);
        const itineraryRef = doc(db, 'itineraries', itineraryId);
        const [interviewSnap, itinerarySnap] = await Promise.all([
          getDoc(interviewRef),
          getDoc(itineraryRef),
        ]);

        if (cancelled) return;

        const interviewContact = interviewSnap.exists() ? interviewSnap.data()?.contact : null;
        const itineraryContact = itinerarySnap.exists() ? itinerarySnap.data()?.contact : null;
        const merged = {
          ...initialContact,
          ...(itineraryContact || {}),
          ...(interviewContact || {}),
        };

        setContact({
          firstName: merged.firstName || '',
          lastName: merged.lastName || '',
          email: merged.email || '',
        });
        setContactLoadError(null);
        setStep('contact');
      } catch (err) {
        if (cancelled) return;
        console.error('[ItineraryPaymentSheet] Failed to load contact info', err);
        setContactLoadError(err instanceof Error ? err.message : 'Unable to load contact details.');
        setStep('contact');
      }
    };

    fetchContact();

    return () => {
      cancelled = true;
    };
  }, [isOpen, itineraryId, versionId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const { body } = document;
    if (!body) return undefined;
    const previous = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = previous;
    };
  }, [isOpen]);

  const amountLabel = useMemo(() => formatAmount(intent?.amount, intent?.currency), [intent]);

  const elementOptions = useMemo(() => ({
    clientSecret: intent?.clientSecret,
    appearance: {
      theme: 'night',
      variables: {
        colorText: '#F8FAFC',
        colorBackground: '#0F172A',
        colorPrimary: '#34D399',
        colorDanger: '#F87171',
        fontFamily: 'Inter, ui-sans-serif, system-ui',
      },
    },
  }), [intent]);

  const handlePaymentSuccess = useCallback((paymentIntent) => {
    setError(null);
    if (onSuccess) onSuccess(paymentIntent);
  }, [onSuccess]);

  const handleContactSubmit = useCallback(async (event) => {
    event.preventDefault();
    const trimmed = {
      firstName: contact.firstName.trim(),
      lastName: contact.lastName.trim(),
      email: contact.email.trim(),
    };

    const nextErrors = {};
    if (!trimmed.firstName) nextErrors.firstName = 'First name is required.';
    if (!trimmed.lastName) nextErrors.lastName = 'Last name is required.';
    if (!trimmed.email) {
      nextErrors.email = 'Email is required.';
    } else if (!emailRegex.test(trimmed.email)) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    setContactErrors(nextErrors);
    setPromoError(null);
    if (Object.keys(nextErrors).length > 0) return;

    setStep('intent-loading');
    setStatus('loading');
    setError(null);

    try {
      const updateContact = httpsCallable(functions, 'updateItineraryContact');
      await updateContact({ itineraryId, contact: trimmed });

      const trimmedCode = promoCode.trim();
      if (trimmedCode) {
        try {
          const redeem = httpsCallable(functions, 'redeemPromoCode');
          const redemption = await redeem({ itineraryId, versionId, code: trimmedCode, contact: trimmed, islandKey: island });
          setIntent(null);
          setStatus('succeeded');
          setStep('payment');
          handlePaymentSuccess({
            id: `promo:${trimmedCode.toUpperCase()}`,
            status: 'succeeded',
            object: 'promo',
            editToken: redemption.data?.editToken || null,
          });
          return;
        } catch (redeemError) {
          console.error('[ItineraryPaymentSheet] redeemPromoCode failed', redeemError);
          const message = redeemError?.message || (redeemError instanceof Error ? redeemError.message : 'That code did not work. Please check it and try again.');
          setPromoError(message);
          setStatus('idle');
          setStep('contact');
          if (onError) onError(redeemError);
          return;
        }
      }

      const callable = httpsCallable(functions, 'createPaymentIntent');
      const result = await callable({ itineraryId, versionId, islandKey: island });

      setIntent(result.data);
      setStatus('ready');
      setStep('payment');
    } catch (err) {
      console.error('[ItineraryPaymentSheet] Failed to start checkout', err);
      const message = err instanceof Error ? err.message : 'Unable to start checkout. Please try again.';
      setError(message);
      setIntent(null);
      setStatus('idle');
      setStep('contact');
      if (onError) onError(err);
    }
  }, [contact, handlePaymentSuccess, island, itineraryId, onError, promoCode, versionId]);

  if (!isOpen) return null;

  const submitLabel = showPromo && promoCode.trim()
    ? 'Redeem & unlock'
    : 'Continue to payment';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-8">
      <div className="w-full max-w-lg rounded-lg bg-slate-900/90 border border-white/10 shadow-xl backdrop-blur-xl p-6">
        <div className="mb-4 space-y-1">
          <h2 className="text-xl font-semibold text-white">Unlock the rest of your itinerary</h2>
          {amountLabel && status !== 'succeeded' && (
            <p className="text-sm text-emerald-300">{amountLabel} will be charged today.</p>
          )}
        </div>

        {step === 'contact-loading' && (
          <div className="text-sm text-white/80">Loading your traveler details…</div>
        )}

        {step === 'contact-error' && (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-red-400/40 bg-red-500/10 px-3 py-2 text-red-200">
              {error || 'Checkout is temporarily unavailable. Please try again later.'}
            </div>
            <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
          </div>
        )}

        {step === 'contact' && (
          <ContactForm
            contact={contact}
            onChange={setContact}
            onSubmit={handleContactSubmit}
            onCancel={onClose}
            saving={status === 'loading'}
            error={error}
            validationErrors={contactErrors}
            loadError={contactLoadError}
            showPromo={showPromo}
            onTogglePromo={() => {
              setShowPromo((prev) => !prev);
              setPromoError(null);
              if (showPromo) setPromoCode('');
            }}
            promoCode={promoCode}
            onPromoChange={(value) => {
              setPromoCode(value);
              setPromoError(null);
            }}
            promoError={promoError}
            submitLabel={submitLabel}
          />
        )}

        {step === 'intent-loading' && (
          <div className="space-y-4 text-sm text-white/80">
            <p>Saving your details and preparing secure checkout…</p>
            <div className="h-1 w-full overflow-hidden rounded bg-white/10">
              <div className="h-full w-1/2 animate-pulse rounded bg-emerald-400/70" />
            </div>
          </div>
        )}

        {step === 'payment' && intent?.clientSecret && stripePromise && status !== 'succeeded' && (
          <Elements stripe={stripePromise} options={elementOptions}>
            <PaymentForm
              intent={intent}
              onSuccess={handlePaymentSuccess}
              onClose={onClose}
              setStatus={setStatus}
              setError={setError}
            />
          </Elements>
        )}

        {status === 'succeeded' && (
          <div className="space-y-4 text-sm text-white/90">
            <p>Access confirmed! We’re preparing the remaining days of your itinerary. It will take several minutes to complete.</p>
            <button type="button" className="btn-primary-cta" onClick={onClose}>Close</button>
          </div>
        )}

        {status === 'ready' && error && step === 'payment' && (
          <div className="mt-4 rounded-md border border-red-400/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
